"use client";

import Link from "next/link";
import { MARKETING_HOME } from "~/lib/inbound-gpt/paths";
import { getMarketingChrome } from "~/lib/siteglue/marketing-chrome";
import { adaptMarketingCopyForBrand } from "~/lib/siteglue/adapt-marketing-copy";

type LegalSection = {
  title: string;
  paragraphs: string[];
};

function LegalDocumentPage({
  title,
  updated,
  intro,
  sections,
}: {
  title: string;
  updated: string;
  intro: string;
  sections: LegalSection[];
}) {
  const chrome = getMarketingChrome();
  const brand = chrome.brandId;
  const a = (s: string) => adaptMarketingCopyForBrand(s, brand);
  const supportEmail =
    brand === "siteglue" ? "support@siteglue.ai" : "support@easygpt.builders";

  return (
    <div className="border-b bg-muted/30 py-14 md:py-20">
      <div className="container mx-auto max-w-3xl px-4">
        <Link
          href={MARKETING_HOME}
          className="mb-8 inline-block text-sm font-medium text-primary hover:underline"
        >
          ← Back to home
        </Link>
        <h1 className="text-3xl font-bold tracking-tight text-foreground md:text-4xl">{a(title)}</h1>
        <p className="mt-3 text-sm text-muted-foreground">Last updated: {updated}</p>
        <p className="mt-6 text-base leading-relaxed text-muted-foreground">{a(intro)}</p>

        <div className="mt-10 space-y-10">
          {sections.map((section) => (
            <section key={section.title}>
              <h2 className="text-xl font-semibold text-foreground">{a(section.title)}</h2>
              <div className="mt-3 space-y-3">
                {section.paragraphs.map((paragraph) => (
                  <p key={paragraph.slice(0, 48)} className="leading-relaxed text-muted-foreground">
                    {a(paragraph)}
                  </p>
                ))}
              </div>
            </section>
          ))}
        </div>

        <p className="mt-12 text-sm text-muted-foreground">
          Questions? Contact{" "}
          <a href={`mailto:${supportEmail}`} className="font-medium text-primary hover:underline">
            {supportEmail}
          </a>
          .
        </p>
      </div>
    </div>
  );
}

export function PrivacyPolicyPageContent() {
  return (
    <LegalDocumentPage
      title="Privacy Policy"
      updated="July 10, 2026"
      intro="Easy GPT Builders (“Inbound GPT”, “we”, “us”) respects your privacy. This policy describes how we collect, use, and protect information when you use www.easygpt.builders, app.easygpt.builders, and related services."
      sections={[
        {
          title: "Information we collect",
          paragraphs: [
            "We collect information you provide when you create an account, hire an AI agent, or contact support — such as name, email, phone number, and business details.",
            "We also collect usage data (pages visited, features used, conversation metadata) and technical data (IP address, browser type, device identifiers) to operate and improve the platform.",
            "If you opt in to SMS on a customer website widget or otherwise provide a mobile number for text messaging, we collect your phone number and message content needed to deliver and support those messages.",
            "If you connect Google services (for example Google Calendar), we receive OAuth tokens and the calendar or account data needed to provide scheduling features you enable.",
          ],
        },
        {
          title: "How we use information",
          paragraphs: [
            "We use your information to provide AI agent services, authenticate users, process billing, send service notifications, and improve product performance.",
            "We do not sell your personal information. We may share data with subprocessors (hosting, email, telephony, SMS carriers, EMR integrations, and AI model providers listed below) only as needed to deliver the service.",
          ],
        },
        {
          title: "Google API user data & Limited Use",
          paragraphs: [
            "The use and transfer of raw or derived user data received from Google APIs will adhere to the Google API Services User Data Policy, including the Limited Use requirements.",
            "Google user data obtained via Google APIs (such as Google Calendar) is used only to provide and improve user-facing scheduling features you request. We do not use Google user data to train generalized AI/ML models.",
            "When Google Calendar data is processed by an AI model to help book or check availability, we route that processing only through compliant third-party AI providers (currently Anthropic and/or OpenAI under their applicable data-processing terms). We do not send Google API user data to AI providers whose terms allow use of submitted content to train their general models (for example DeepSeek).",
            "OAuth access and refresh tokens for Google are stored securely and used only to call Google APIs on your behalf. A public statement of this Limited Use commitment is also available at https://www.easygpt.builders/google-api-user-data.",
          ],
        },
        {
          title: "AI model providers",
          paragraphs: [
            "Depending on your project configuration, conversations and related workflows may be processed by third-party AI services including Anthropic, OpenAI, DeepSeek, xAI, Groq, and Google Gemini. For features that use Google API user data, only Anthropic and/or OpenAI are used, as described above.",
          ],
        },
        {
          title: "SMS / text messaging",
          paragraphs: [
            "Easy GPT Builders enables businesses to communicate with their customers by SMS through our platform (for example, responses to a “Text me” request on a website widget, appointment-related messages, and follow-ups configured by the business).",
            "You may opt in by checking a consent box on a web form, submitting your mobile number through an approved widget, or other consent methods permitted by law. Message frequency varies by business and use case.",
            "Message and data rates may apply. Reply STOP to cancel text messages from a program at any time. Reply HELP for help. After you send STOP, you may receive one final confirmation message.",
            "We do not sell or share mobile phone numbers or SMS opt-in data with third parties for their own marketing or promotional purposes.",
            "Carriers are not liable for delayed or undelivered messages.",
          ],
        },
        {
          title: "Data retention & security",
          paragraphs: [
            "We retain account and conversation data while your account is active and as required for legal, billing, or security purposes.",
            "We apply industry-standard safeguards including encryption in transit, access controls, and monitoring. No method of transmission over the Internet is 100% secure.",
          ],
        },
        {
          title: "Your choices",
          paragraphs: [
            "You may request access, correction, or deletion of your account data by contacting support@easygpt.builders.",
            "You can opt out of non-essential marketing emails using the unsubscribe link in those messages.",
            "You can opt out of SMS at any time by replying STOP to a message you received, or by contacting the business that sent the message or support@easygpt.builders.",
            "You may disconnect Google integrations at any time from your project settings; we will stop using those Google API credentials for new requests.",
          ],
        },
      ]}
    />
  );
}

export function GoogleApiUserDataPageContent() {
  return (
    <LegalDocumentPage
      title="Google API Services User Data"
      updated="July 10, 2026"
      intro="This page states how Easy GPT Builders / Inbound GPT handles user data received from Google APIs."
      sections={[
        {
          title: "Limited Use commitment",
          paragraphs: [
            "The use and transfer of raw or derived user data received from Google APIs will adhere to the Google API Services User Data Policy, including the Limited Use requirements.",
          ],
        },
        {
          title: "AI processing of Google user data",
          paragraphs: [
            "We use third-party AI services (not in-house or self-hosted models) to power conversational agents.",
            "When Google user data obtained via Google APIs (for example Google Calendar busy times used for appointment booking) is processed by an AI model, that processing is limited to Anthropic and/or OpenAI. We do not transfer Google API user data to DeepSeek or other AI providers that reserve rights to train general models on submitted content.",
            "Google OAuth tokens remain on our servers and are not sent to AI providers. Calendar event titles and descriptions from Google Calendar are not forwarded to AI models; only busy/free time ranges needed for scheduling are provided.",
          ],
        },
        {
          title: "Related policies",
          paragraphs: [
            "See also our Privacy Policy at https://www.easygpt.builders/privacy-policy for full details on collection, retention, and your choices.",
          ],
        },
      ]}
    />
  );
}

export function TermsOfServicePageContent() {
  return (
    <LegalDocumentPage
      title="Terms of Service"
      updated="June 23, 2026"
      intro="These Terms of Service (“Terms”) govern your access to and use of Inbound GPT and Easy GPT Builders services at www.easygpt.builders and app.easygpt.builders. By creating an account or using the platform, you agree to these Terms."
      sections={[
        {
          title: "Service description",
          paragraphs: [
            "Inbound GPT provides AI-powered communication tools including phone, chat, SMS, and web agents for businesses. Features vary by plan and configuration.",
            "You are responsible for configuring agents appropriately for your industry, including consent, disclosures, and compliance with applicable laws.",
          ],
        },
        {
          title: "SMS / text messaging",
          paragraphs: [
            "Our platform may send and receive SMS and text messages on behalf of business customers (for example, responses to a “Text me” request on a website widget, appointment reminders, and follow-ups configured by the business). End users must provide valid consent before receiving marketing or automated texts, as required by applicable law and carrier rules (including 10DLC registration in the United States).",
            "Message frequency varies by business and use case. Message and data rates may apply. Reply STOP to cancel text messages from a program at any time. Reply HELP for help. After you send STOP, you may receive one final confirmation message.",
            "Business customers are responsible for truthful opt-in flows, required disclosures (including message frequency, “Msg & data rates may apply”, and STOP/HELP instructions), and honoring opt-out requests promptly.",
            "Carriers are not liable for delayed or undelivered messages.",
            "Easy GPT Builders may suspend or limit SMS or text messaging features for accounts that send unlawful, deceptive, or non-compliant messages.",
          ],
        },
        {
          title: "Accounts & acceptable use",
          paragraphs: [
            "You must provide accurate registration information and keep credentials secure. You are responsible for activity under your account.",
            "You may not use the service for unlawful activity, spam, harassment, or attempts to bypass security or usage limits.",
          ],
        },
        {
          title: "Fees & billing",
          paragraphs: [
            "Paid plans are billed according to the pricing shown at checkout or in your account. Fees are non-refundable except where required by law or stated in writing.",
            "We may change pricing with reasonable notice. Continued use after a price change constitutes acceptance.",
          ],
        },
        {
          title: "Limitation of liability",
          paragraphs: [
            "The service is provided “as is” to the maximum extent permitted by law. We are not liable for indirect, incidental, or consequential damages arising from use of the platform.",
            "Our total liability for any claim relating to the service is limited to the amount you paid us in the twelve months before the claim.",
          ],
        },
        {
          title: "Changes",
          paragraphs: [
            "We may update these Terms from time to time. Material changes will be posted on this page with an updated date. Your continued use after changes constitutes acceptance.",
          ],
        },
      ]}
    />
  );
}
